import React from 'react';
import DateFilter from 'reusable/datafilter';
import Export from 'reusable/export';
import ExportRangeDialog, { ExportRange } from 'reusable/ExportRangeDialog';
import MonthNavigator from 'reusable/MonthNavigator';
import {
  bundledDeviceTimeZoneExportFields,
  CsvCondition,
  csvDateTime,
  deviceTimeZoneExportFields,
} from 'config/csv';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import { useTranslation } from 'react-i18next';

interface ToolbarProps {
  searchValue: string;
  sortValue: string;
  dateValue: string;
  currentPage: number;
  onSearchChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onDateChange: (value: string) => void;
  /** A28（2026-11-03 和田様の UAT）。「全ての提出」「上限超過」で表示している月（`YYYY-MM`）。 */
  month: string;
  onMonthChange: (month: string) => void;
  onPageChange: (value: number) => void;
  /** 「最近の提出」の CSV に出す行（表示中のもの）。 */
  DataForExport: any[];
  /** A28。「全ての提出」「上限超過」のエクスポートの範囲（この月／期間／全件）の記録を全ページ読む。 */
  fetchExportRows: (range: ExportRange) => Promise<any[]>;
  activetab: string;
}

const Toolbar: React.FC<ToolbarProps> = ({
  searchValue,
  sortValue,
  dateValue,
  currentPage,
  onSearchChange,
  onSortChange,
  onDateChange,
  month,
  onMonthChange,
  onPageChange,
  DataForExport,
  fetchExportRows,
  activetab,
}) => {
  const { t } = useTranslation(['alcohol', 'common']);
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.create-time', { ns: 'common' }), value: 'create_time' },
  ];
  const handleSortChange = (value: string) => {
    onSortChange(value);
    onPageChange(1);
  };

  const handleSearchChange = (value: string) => {
    onSearchChange(value);
    onPageChange(1);
  };

  const handleDateChange = (value: string) => {
    onDateChange(value);
    onPageChange(1);
  };

  const exportFields = [
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、測定記録の ID を先頭に出す。
    { header: 'Test ID', field: 'id' },
    { header: 'Driver ID', field: 'user.id' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Driver Contact', field: 'user.phone' },
    // EV-450。消去の依頼で氏名だけを残した運転者（法令で残す測定記録）。
    { header: 'Driver Erased', field: 'user.is_erased', customExport: (erased: unknown) => (erased ? 'Erased' : '') },
    { header: 'Test Results mg/l', field: 'data' },
    // ADM-05。測定日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date Time', field: 'testing_time', customExport: csvDateTime },
    // EV-116 段階3。測定の時刻の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];

  const exportFieldsForRecentSubmission = [
    { header: 'Driver ID', field: 'id' },
    { header: 'Driver Name', field: 'name' },
    { header: 'Driver Contact', field: 'phone' },
    // EV-141。運転者単位の行を保ち、測定値と同じ順序で元の測定IDを出す。
    {
      header: 'Test IDs',
      field: 'alcoholtests',
      customExport: (tests: any[]) =>
        Array.isArray(tests) ? tests.map((test) => test.id ?? '').join('; ') : '',
    },
    {
      header: 'Test Result',
      field: 'alcoholtests',
      customExport: (tests: any[]) => {
        if (!Array.isArray(tests)) return '';
        return tests.map((test) => `${test.data}`).join('; ');
      },
    },
    // RV-325（EV-116 段階3）。測定ごとの端末の時間帯を Test IDs と同じ順に束ねる（無い測定は空）。
    ...bundledDeviceTimeZoneExportFields('alcoholtests'),
  ];

  // ADM-11-AC2。出力した条件をファイル名に入れる（「最近の提出」は表示中のページを出す）。
  const exportConditions: CsvCondition[] = [
    ['date', dateValue],
    ['tab', activetab],
    ['search', searchValue],
    ['sort', sortValue],
    ['page', currentPage],
  ];
  // A28。測定の一覧の範囲（period）はエクスポートの画面で足す。
  const rangeExportConditions: CsvCondition[] = [
    ['tab', activetab],
    ['search', searchValue],
    ['sort', sortValue],
  ];

  // 「最近の提出」は1日ごとの提出の状況（運転者ごとの行）なので、今までどおり日付で選び、表示中の行を出す。
  const isRecent = activetab === 'recentsubmissions';

  return (
    // EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
    // 追加・エクスポートのボタンの文字は折らない（reusable/addButton・export）。
    // EV-339。1024px 以上は右の組を縮めない（lg:shrink-0。機器・車両のツールバーと同じ）。以前は右の組が中身より狭く縮み、
    // 「エクスポート」がカードの右の端から出ていた。
    <section className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between w-full">
      {/* 2026-11-04 PM。月の切り替え（「当日の測定状況」は日付の選択）は、車両の点検履歴・ドライバー管理と同じく
          表の上の行の左に置き、その右に検索。右の組はソートとエクスポート。 */}
      <div className="flex flex-row flex-1 items-center gap-2">
        {isRecent ? (
          <DateFilter defaultDate={dateValue} onDateChange={handleDateChange} />
        ) : (
          <MonthNavigator month={month} onChange={onMonthChange} />
        )}
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
      </div>
      <div className="ml-auto flex flex-row lg:shrink-0 space-x-2 items-center">
        <SortBy
          options={sortingOptions}
          defaultValue={sortValue}
          onChange={handleSortChange}
        />
        {isRecent ? (
          <Export
            data={DataForExport}
            exportFields={exportFieldsForRecentSubmission}
            name="alcohol_tests"
            conditions={exportConditions}
          />
        ) : (
          <ExportRangeDialog
            month={month}
            exportFields={exportFields}
            name="alcohol_tests"
            conditions={rangeExportConditions}
            fetchRows={fetchExportRows}
          />
        )}
      </div>
    </section>
  );
};

export default Toolbar;
